/**
 * 部件条的「所见即所得」小头像（A 档，2026-10-04 用户拍板）：
 * 每枚部件选项的缩略图 = 把当前配置的**本类目槽**换成该件后，整张脸渲出的一张 PNG。
 *
 * ⛔ 复用 `gamemount.avatarPngSrc`：那张表按 `size|config` 全局常驻且无上限，
 * 而捏脸时每点开一个类目就要塞进 N 条永不淘汰的条目（用户问的「一直换装会不会攒很多图」正指这里）。
 * 本表是**页内**缓存：LRU 封顶 `THUMB_CACHE_MAX` 条，面板 `destroy()` 调 `clearThumbCache()` 交回去。
 *
 * 一张整脸小图的出图成本 ≈ 给一个 NPC 出头像（SVG-as-image 里要解 10 张 1200×1200 精灵），
 * 所以条内**点选只挪高亮、⛔ 重建整条**（判据见 `categories.syncStripState`）：
 * 一次点选改的就是本槽，而本槽每个取值在条里已经各摆了一张图 ⇒ 重画＝白出 N 张。
 */
import { AvatarConfig } from './config';
import { ensureSprites, rasterizeAvatar } from './gamemount';
import { spriteRawUrl } from './sprites';

/** 出图边长：88 CSS px（.cc-opt-face 的 .cc-thumb）× DPR 封顶 3 ≈ 264，取 256 */
export const THUMB_PX = 256;
const THUMB_CACHE_MAX = 64;

const cache = new Map<string, string>();
const pending = new Map<string, Promise<string>>();
/** key → 正在等这张图的 `<img>`（⛔ 只认第一个请求者：面板 `refresh()` 会二次重建整条，
 *  新条的 img 与旧条同 key、共用同一条在途 promise，若把过期判据绑在旧 img 上就会把新 img 一起误跳过 ⇒ 永远停在占位） */
const waiters = new Map<string, Set<HTMLImageElement>>();
let generated = 0;
let hits = 0;
let skipped = 0;
/** 代际号：清表后仍在途的那几张渲完时⛔ 写回表（不然关页又给攒回来一坨） */
let generation = 0;

/**
 * 串行出图队列：一张一张渲，⛔ 8 张同时开。
 * 每次出图要在 SVG-as-image 里解 10 张 1200×1200 精灵（约 58MB 瞬时位图），并行＝峰值按件数乘；
 * 串行把峰值压回一张，代价是整条画满的尾帧略长（实测见 `_test_avatar_thumb_live.mjs`）。
 * 尾队列⛔ 被一条失败卡死 ⇒ 每个 worker 自己吞异常。
 */
let queueTail: Promise<unknown> = Promise.resolve();
function enqueue(job: () => Promise<string>): Promise<string> {
    const run = queueTail.then(job, job);
    queueTail = run.catch(() => undefined);
    return run;
}

/** 整脸小图 dataURL（同配置命中缓存并把它挪到 LRU 最新端） */
export function avatarThumbSrc(config: AvatarConfig): Promise<string> {
    const key = JSON.stringify(config);
    const done = cache.get(key);
    if (done) {
        hits++;
        cache.delete(key);
        cache.set(key, done);
        return Promise.resolve(done);
    }
    const inflight = pending.get(key);
    if (inflight) { hits++; return inflight; }

    const gen = generation;
    const p = enqueue(async () => {
        // 排到队首时屏上已没有一张图在等这个配置 ⇒ 跳过，把队列让给在屏的那一条
        const w = waiters.get(key);
        if (!w || [...w].every(el => !el.isConnected)) { skipped++; throw new Error('thumb job superseded'); }
        await ensureSprites();
        return rasterizeAvatar(config, THUMB_PX);
    }).then(
        src => {
            generated++;
            pending.delete(key);
            if (gen !== generation) throw new Error('thumb cache cleared mid-flight');
            cache.set(key, src);
            while (cache.size > THUMB_CACHE_MAX) cache.delete(cache.keys().next().value as string);
            return src;
        },
        // 出图失败/跳过/清表都要把在途条目摘掉，否则这张坏配置永久卡在 pending，再点只拿到同一个 rejection
        err => { pending.delete(key); throw err; },
    );
    pending.set(key, p);
    return p;
}

/**
 * 条内一枚部件选项的缩略图：先挂现有的裸部件图当占位（无精灵件先藏住框），
 * 整脸小图渲好后就地换 src ⇒ ⛔ 空框、⛔ 跳版（同框同尺寸，只换图）。
 * 出图失败/精灵未就绪 ⇒ 占位原样留着，⛔ 退回空框。
 */
export function buildStripThumb(opt: HTMLElement, part: object, config: AvatarConfig): void {
    const img = document.createElement('img');
    img.className = 'cc-thumb';
    const url = spriteRawUrl(part);
    if (url) img.src = url;
    else img.classList.add('cc-thumb-hidden');
    opt.appendChild(img);
    const key = JSON.stringify(config);
    let w = waiters.get(key);
    if (!w) waiters.set(key, w = new Set<HTMLImageElement>());
    w.add(img);
    avatarThumbSrc(config)
        .then(src => {
            // 同一个配置可能有好几张在等（`refresh()` 二次重建整条就是这种），逐个落图、掉档的顺手摘掉
            w!.forEach(el => {
                if (!el.isConnected) { w!.delete(el); return; }
                el.src = src;
                el.classList.remove('cc-thumb-hidden');
            });
            if (!w!.size) waiters.delete(key);
        })
        .catch(() => {});
}

/** 离开捏脸页：缩略图全丢（NPC 头像那张全局缓存⛔ 受此影响） */
export function clearThumbCache(): void {
    generation++;
    cache.clear();
    pending.clear();
    waiters.clear();
}

/** 真机实测与排障用（同 app.ts 的 `_gameRef` 先例，⛔ 参与任何游戏逻辑） */
Object.defineProperty(globalThis, '_thumbCacheStats', {
    configurable: true,
    get: () => {
        let bytes = 0;
        cache.forEach(v => { bytes += v.length; });
        return { entries: cache.size, pending: pending.size, generated, hits, skipped, bytes, max: THUMB_CACHE_MAX, px: THUMB_PX };
    },
});
